import React from "react";
import styled from "@emotion/styled";

import { useBuyersInLists } from "lib/hooks/api/buyer_lists/useBuyerLists";
import { routes } from "lib/routes";
import { OpportunityDetails } from "../../../lib/hooks/api/opportunities/useOpportunity";
import { sysBackgroundAlternative, sysTextSecondary } from "../../../lib/themes/colors";
import { OpportunityEntityType } from "../../../lib/types/graphQLEnums";
import { Flex, Text } from "../../../styles/utility-components";
import { opportunitySavedCardContainer, opportunitySavedCardIcon } from "./shared/styles";
import EntityCardContent from "./EntityCardContent";
import FindEntity from "./FindEntity";
import Tag from "./Tag";
import { Logo } from "./utils";

type Props = {
  buyers: OpportunityDetails["buyers"];
  opportunityId: string;
};

export default function OpportunityBuyers({ buyers, opportunityId }: Props) {
  const handleViewBuyer = (id: string) => () => {
    window.location.href = `/buyers/${id}`;
  };

  return (
    <Flex column gap={16}>
      {buyers.map((buyer) => (
        <BuyerCard
          key={buyer.id}
          buyer={buyer}
          onView={handleViewBuyer(buyer.id)}
          opportunityId={opportunityId}
        />
      ))}
      <FindEntity entity="buyer" url={routes.buyers} isEmptyState={!buyers.length} />
    </Flex>
  );
}

function BuyerCard({
  buyer,
  onView,
  opportunityId,
}: {
  buyer: OpportunityDetails["buyers"][0];
  onView: () => void;
  opportunityId: string;
}) {
  const {
    savedState: { savedListNames: listsWithBuyer },
  } = useBuyersInLists([buyer.id]);

  return (
    <Container onClick={onView}>
      <EntityCardContent
        entity="buyer"
        entityId={buyer.id}
        entityType={OpportunityEntityType.Organisation}
        entityName={buyer.name}
        opportunityId={opportunityId}
      >
        <ContentWrapper column gap={16}>
          <Flex gap={12} alignItems="center">
            <Icon>
              <Logo url={buyer.logoUrl} />
            </Icon>
            <Text fontWeight="600">{buyer.name || "Untitled Buyer"}</Text>
          </Flex>
          <Flex gap={8} flexWrap="wrap">
            {listsWithBuyer.map((buyerList) => (
              <Tag
                key={buyerList}
                title={`Saved to ${buyerList}`}
                icon="bookmark"
                iconProps={{ fill: true }}
              />
            ))}
            {hasCategories(buyer) && (
              <Tag
                backgroundColor={sysBackgroundAlternative}
                fontColor={sysTextSecondary}
                title={
                  <CategoryLabel
                    categoryName={buyer.categoryAssignments[0].categoryName}
                    parentCategoryName={buyer.categoryAssignments[0].parentCategoryName}
                  />
                }
              />
            )}
          </Flex>
        </ContentWrapper>
      </EntityCardContent>
    </Container>
  );
}

type BuyerWithCategory = {
  id: string;
  name: string;
  categoryAssignments: {
    categoryName: string;
    parentCategoryName: string;
  }[];
};

function hasCategories(buyer: OpportunityDetails["buyers"][0]): buyer is BuyerWithCategory {
  return !!buyer.categoryAssignments && buyer.categoryAssignments.length > 0;
}

function CategoryLabel({
  categoryName,
  parentCategoryName,
}: {
  categoryName: string;
  parentCategoryName?: string;
}) {
  return (
    <CategoryWrapper>
      {parentCategoryName && (
        <>
          <Text inline description color={sysTextSecondary}>
            {parentCategoryName}
          </Text>
          <Divider>&gt;</Divider>
        </>
      )}
      <SubcategoryText inline description color={sysTextSecondary}>
        {categoryName}
      </SubcategoryText>
    </CategoryWrapper>
  );
}

const Container = styled(Flex)(({ theme }) => ({
  ...opportunitySavedCardContainer(theme),
}));

const ContentWrapper = styled(Flex)({ minWidth: 0 });

const Icon = styled(Flex)(({ theme }) => ({
  ...opportunitySavedCardIcon(theme),
}));

const CategoryWrapper = styled.div(({ theme }) => ({
  color: theme.colors.sysTextSecondary,
  fontSize: 12,
  fontWeight: 400,
  textOverflow: "ellipsis",
  overflow: "hidden",
}));

const Divider = styled.span(() => ({
  margin: "0px 4px",
}));

const SubcategoryText = styled(Text)({ whiteSpace: "nowrap" });
